<script setup>
import { computed } from 'vue';

const props = defineProps({
  label: { type: String, required: true },
  forId: { type: String, default: '' },
  testId: { type: String, default: '' },
  filled: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  error: { type: String, default: '' },
  required: { type: Boolean, default: false },
  showSuccess: { type: Boolean, default: true },
  indicator: {
    type: String,
    default: 'success',
    validator: value => ['success', 'select', 'none'].includes(value),
  },
  variant: {
    type: String,
    default: 'input',
    validator: value => ['input', 'textarea'].includes(value),
  },
});

const labelText = computed(() =>
  props.required
    ? `${props.label}${props.label.endsWith('*') ? '*' : ' *'}`
    : props.label
);
const fieldClasses = computed(() => {
  if (props.variant === 'textarea') {
    if (props.error) return 'border border-[#F9D2D2] bg-white';
    if (props.filled) return 'border-0 bg-[#EDF8F7]';
    return 'border border-[#E0E7EB] bg-white';
  }
  if (props.error) return 'border border-[#F9D2D2] bg-white';
  if (props.filled && props.disabled) return 'bg-[#F1F4F5]';
  if (props.filled) return 'bg-[#EDF8F7]';
  return 'border border-[#E0E7EB] bg-white';
});
const labelClasses = computed(() => {
  if (props.error) return 'text-[#D64545]';
  if (props.filled && !props.disabled) return 'text-[#009F94]';
  if (props.variant === 'textarea') return 'text-[#464F53]';
  return 'text-[#7F8C93]';
});
</script>

<template>
  <label
    :for="forId || undefined"
    class="relative flex min-w-0 flex-col font-roboto"
    :class="[
      variant === 'textarea'
        ? 'min-h-[118px] justify-start rounded-[12px_12px_0_12px] p-0'
        : 'min-h-[82px] justify-center rounded-[10px] px-4 pt-3',
      fieldClasses,
    ]"
    :data-filled="filled"
    :data-test="testId || undefined"
  >
    <span
      :class="[
        variant === 'textarea'
          ? 'absolute top-0 z-10 p-4 text-xl leading-6'
          : 'w-[calc(100%+24px)] truncate whitespace-nowrap text-xs leading-4',
        labelClasses,
      ]"
    >
      {{ labelText }}
    </span>
    <span
      :class="
        variant === 'textarea'
          ? 'flex min-h-0 w-full flex-1'
          : 'relative flex min-w-0 items-center'
      "
    >
      <slot />
      <span
        v-if="
          variant !== 'textarea' &&
          filled &&
          showSuccess &&
          indicator === 'success' &&
          !error
        "
        class="i-lucide-check absolute right-0 size-5 text-[#009F94]"
        aria-hidden="true"
      />
      <span
        v-else-if="indicator === 'select' && !error"
        class="i-lucide-chevron-down absolute right-0 size-4 text-[#009F94]"
        aria-hidden="true"
      />
    </span>
    <span v-if="error" class="mt-1 text-xs text-[#D64545]">{{ error }}</span>
  </label>
</template>
